/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import store from 'app/store';
import {
  fetchChatbotConversations,
  pollChatbotMessages
} from 'features/chatbot/chatbot-thunks';
import {
  setActiveConfig,
  setProtectedConfigurationIdsDuringGeneration
} from 'features/configurations/configurations-slice';
import axios from 'utils/api';
import {
  collectProtectedConfigurationIds,
  shouldKeepActiveConfigurationAfterGeneration
} from 'utils/configuration-generation';
import {
  refetchInstanceConfigurations,
  refetchStructureLevels
} from 'utils/data';
import { hybridLeftoverUserPromptSuffix } from 'utils/hybrid-config-generation-prompt';
import { notification } from 'utils/ui';
import {
  CreateLevelsRequestType,
  setConfigGenerating,
  setLevelsLoading,
  setPollingStatus
} from './ai-slice';

export const createLevelsFromProjectSettings = createAsyncThunk(
  'ai/createLevelsFromProjectSettings',
  async (data: CreateLevelsRequestType) => {
    try {
      notification('Auto generating levels...', 'warning');
      store.dispatch(setLevelsLoading(true));
      store.dispatch(setPollingStatus('polling'));

      const response = await axios.post<{
        status: string;
        conversationUniqueId: string;
      }>('ai/chatbot/run-category-sub-agents', {
        structureUniqueId: data.structureUniqueId,
        projectId: data.projectId,
        instanceUniqueId: data.instanceUniqueId,
        categoryCode: 'level_auto_generation',
        prompt:
          'Create all levels for this structure based on project settings.',
        title: 'Auto generate levels'
      });

      await pollChatbotMessages(
        response.data.conversationUniqueId,
        data.structureUniqueId,
        data.projectId,
        0
      );

      await refetchStructureLevels(data.structureUniqueId);
      notification('Levels generated', 'success');
      store.dispatch(setLevelsLoading(false));
      store.dispatch(setPollingStatus('completed'));
    } catch (error) {
      notification('Failed to auto generate levels', 'error');
      store.dispatch(setLevelsLoading(false));
      store.dispatch(setPollingStatus('idle'));
      throw error;
    }
  }
);

// Poll budget for config generation. Server worst case: sub-agent soft deadline
// 300s (checked before the AI call, so a final round can overshoot) + one AI call
// ~60s + a batched round of up to 3 sequential add-condensers-and-zones calls at
// the 120s MCP cap (360s) ≈ 720s, plus pre-run context fetch and post-settlement
// marking margin on a cold cache. The server watchdog turns a dead run 'failed'
// within ~10 min of its last message, so this budget terminates via status, not
// client timeout, unless the server is healthy-but-slow — when waiting is correct.
export const CONFIG_GENERATION_POLL_TIMEOUT_MS = 15 * 60 * 1000;

let currentConfigGenerationController: AbortController | null = null;

// Outcome contract (AHA-6784 B4): 'ok' = the run completed and produced at
// least one AI config, 'empty' = the run completed but produced no AI
// configs, 'failed' = no generation happened or the run failed, 'aborted' =
// superseded/cancelled. The function never throws.
export type GenerateConfigurationsOutcome =
  | 'ok'
  | 'empty'
  | 'failed'
  | 'aborted';

export async function generateConfigurations(): Promise<GenerateConfigurationsOutcome> {
  const { structureUniqueId } = store.getState().structure;
  const { projectDetails, instanceUniqueId } = store.getState().project;

  // Generation never started and never will — 'failed' is the honest signal.
  if (!structureUniqueId || !projectDetails?.projectId) return 'failed';

  if (currentConfigGenerationController) {
    currentConfigGenerationController.abort();
  }
  const controller = new AbortController();
  currentConfigGenerationController = controller;

  const configurationState = store.getState().configurations;
  store.dispatch(
    setProtectedConfigurationIdsDuringGeneration(
      collectProtectedConfigurationIds(
        configurationState.configurations,
        configurationState.locallyCreatedConfigurationIds
      )
    )
  );
  store.dispatch(setConfigGenerating(true));
  notification('Generating AI configurations...', 'warning');

  try {
    const response = await axios.post<{
      status: string;
      conversationUniqueId: string;
    }>(
      'ai/chatbot/run-category-sub-agents',
      {
        structureUniqueId,
        projectId: projectDetails.projectId,
        instanceUniqueId: instanceUniqueId ?? undefined,
        categoryCode: 'config_auto_generation',
        prompt: `Design and build a HVAC system configuration for this project. Use your tools to gather all necessary context and create the full configuration including condensers, zones, air handlers, and line sets.${hybridLeftoverUserPromptSuffix(
          store.getState()
        )}`,
        title: 'Auto generate configuration'
      },
      { signal: controller.signal }
    );

    const finalStatus = await pollChatbotMessages(
      response.data.conversationUniqueId,
      structureUniqueId,
      projectDetails.projectId,
      0,
      controller.signal,
      CONFIG_GENERATION_POLL_TIMEOUT_MS
    );

    if (controller.signal.aborted) return 'aborted';

    if (finalStatus === 'failed') {
      // Refetch first so any configs the run completed (marked aiGenerated
      // incrementally at the tool) become visible despite the failure.
      await refetchInstanceConfigurations();
      notification('AI generation failed — try again', 'error');
      store.dispatch(fetchChatbotConversations({ structureUniqueId }));
      return 'failed';
    }

    const { configurations: configurationsBeforeRefetch, activeConfig } =
      store.getState().configurations;
    const activeConfigurationBeforeRefetch =
      configurationsBeforeRefetch[activeConfig];

    await refetchInstanceConfigurations();

    const latestConfigurationState = store.getState().configurations;
    const latestConfigurations = latestConfigurationState.configurations;
    const activeConfigurationAfterRefetch =
      latestConfigurations[latestConfigurationState.activeConfig];
    const firstAiConfigIndex = latestConfigurations.findIndex(
      (configuration) => configuration.aiGenerated
    );
    const keepCurrentSelection = shouldKeepActiveConfigurationAfterGeneration(
      activeConfigurationBeforeRefetch?.uniqueId,
      activeConfigurationAfterRefetch
    );
    if (firstAiConfigIndex < 0) {
      notification('No AI configurations were generated', 'warning');
      store.dispatch(fetchChatbotConversations({ structureUniqueId }));
      return 'empty';
    }

    if (!keepCurrentSelection) {
      store.dispatch(setActiveConfig(firstAiConfigIndex));
    }
    notification('AI configurations generated', 'success');
    store.dispatch(fetchChatbotConversations({ structureUniqueId }));
    return 'ok';
  } catch {
    if (controller.signal.aborted) return 'aborted';
    notification('Failed to generate AI configurations', 'error');
    return 'failed';
  } finally {
    if (currentConfigGenerationController === controller) {
      currentConfigGenerationController = null;
      store.dispatch(setConfigGenerating(false));
      store.dispatch(setProtectedConfigurationIdsDuringGeneration([]));
    }
  }
}
